// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import moment from 'moment';
import Switch from 'react-switch';

import Select2, { DataSelect2, EventSelect2 } from 'wdna-select2';
import DatePicker, { DateSelectorConfig } from 'wdna-date-selector';

import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { messageError, messageWarning } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { ContextType, FilterContext } from '../../../../../types';
import { typeSensorVariable, TypeSensorVariableDO } from '../../../../configuration/requests';
import { sensor, SensorDO, weather } from '../../../../sensors/requests';
import Report from './Report';

// ID 4 is the weather station type sensor
const TYPE_SENSOR_ID: number = 4;

interface Props {
  rowId?: number;
}

interface State {
  sensorArray: SensorDO[];
  typeVariableArray: TypeSensorVariableDO[];

  dates: Date[];
  flatpickrMode: string;
  groupDateTime: string;

  dateIni: string;
  timeIni: string;
  dateEnd: string;
  timeEnd: string;
  groupTime: string;

  variables: any[];
  modalShow: boolean;
  autoRefresh: boolean;

  id?: number;
}

export default class Filter extends React.Component<Props, State> {
  private id: string | number | undefined;
  private pid: string;
  private mounted: boolean;

  private timer: NodeJS.Timeout | undefined;

  public static contextType: React.Context<ContextType> = FilterContext;
  public context: React.ContextType<typeof FilterContext> | undefined;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;

    const dIni: Date = new Date();
    dIni.setDate(dIni.getDate() - 2);
    const dEnd: Date = new Date();

    this.state = {
      sensorArray: [],
      typeVariableArray: [],

      dates: this.initialDates(),
      flatpickrMode: 'range',
      groupDateTime: '24h',

      dateIni: moment(dIni).add(1, 'days').format('YYYY-MM-DD'),
      timeIni: '00:00',
      dateEnd: moment(dEnd).add(1, 'days').format('YYYY-MM-DD'),
      timeEnd: '00:00',
      groupTime: '10',

      variables: [],
      modalShow: false,
      autoRefresh: true,
    };
  }

  public componentDidMount(): void {
    this.mounted = true;

    this.load().catch(() => { /* pass */ }).finally(() => {
      this.submit().catch(() => { /* pass */ });
      this.refresh(true);
    });
  }

  public componentDidUpdate(): void {
    /* pass */
  }

  public componentWillUnmount(): void {
    this.mounted = false;
    this.refresh(false);
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = async (): Promise<void> => {
    this.loadingStart();
    try {
      const sensorArray: SensorDO[] = await sensor<SensorDO[]>('list', undefined, { typeSensorId: TYPE_SENSOR_ID }) || [];
      const typeVariableArray: TypeSensorVariableDO[] = await typeSensorVariable<TypeSensorVariableDO[]>('list', undefined, { typeSensorId: TYPE_SENSOR_ID }) || [];

      const variableNames: string[] = typeVariableArray.map((row: TypeSensorVariableDO): string => {
        return `${row.variableName} (${row.variableUnit})`;
      });

      this.context?.updateParent({
        typeVariableArray,
        data3: variableNames,
      });

      this.setLocalState({
        id: sensorArray[0].id,
        sensorArray,
        typeVariableArray,
        variables: ['1', '2'],
      });
    } catch (error) {
      messageWarning('Warning', 'There are no weather stations in the system');
    }
    this.loadingFinish();
  };

  public initialDates = (): Date[] => {
    const today: Date = new Date();
    const twoDaysBefore: Date = moment().add(-2, 'days').toDate();
    return [twoDaysBefore, today];
  };

  public hideSidebar = (): void => {
    $('#right-panel').addClass('hidden-functions');
    $('#tab-main-content').removeClass('tab-left-content');
    $('#toggle-func-btn').addClass('transparent sidebar-hidden');
  };

  public submit = async (): Promise<void> => {
    if (!this.mounted) return;
    const { id, dateIni, timeIni, dateEnd, timeEnd, groupTime, variables } = this.state;

    const idSensor: number | undefined = id || this.props.rowId;
    if (!idSensor) {
      messageError('Error', 'Please select a device');
      return;
    }

    // REQUEST: statistics
    const date: Date = new Date(new Date().setHours(0, 0, 0, 0));
    const dIni1: string = moment(date).subtract(14, 'days').toDate().toISOString();
    const dEnd1: string = moment(date).add(1, 'days').toDate().toISOString();

    const req2: Record<string, any> = {
      from: dIni1,
      to: dEnd1,
      values: [0, 1, 2, 2, 4],
      operators: ['amean', 'max', 'max', 'min', 'sum'],
      group: 1440,
      round: true,
    };

    const dIni2: string | undefined = (dateIni) ? moment(`${dateIni}T${timeIni || '00'}:00`, 'YYYY-MM-DDThh:mm').toDate().toISOString() : undefined;
    const dEnd2: string | undefined = (dateEnd) ? moment(`${dateEnd}T${timeEnd || '00'}:00`, 'YYYY-MM-DDThh:mm').toDate().toISOString() : undefined;

    // REQUEST: register_list
    const req1: Record<string, any> = { from: dIni2, to: dEnd2 };

    // REQUEST: register_graph
    const req4: Record<string, any> = { group: groupTime, values: variables, round: false, from: dIni2, to: dEnd2 };

    // SUM aggregation for pluviometry
    const pluvio: string = '4';
    if (variables.includes(pluvio)) {
      const idx: number = variables.indexOf(pluvio);
      req4.operators = Array.from({ length: variables.length }, () => 'mean');
      req4.operators[idx] = 'sum';
    }

    if (this.isValidRequest(req4)) {
      // REQUEST: windrose
      const req5: Record<string, any> = { dir: 0, speed: 1, from: dIni2, to: dEnd2 };

      this.loadingStart();
      try {
        if (this.id !== id) {
          const [info, data2] = await Promise.all([
            sensor<SensorDO>('get', String(idSensor)),
            weather('statistics', String(idSensor), req2)
          ]);
          data2.splice(0, 1); // Remove title row of statistics

          this.context?.updateParent({
            nameStation: info?.description,
            data2,
          });
        }

        const [data1, data4T, data5] = await Promise.all([
          sensor('register_list', String(idSensor), req1),
          sensor('register_graph', String(idSensor), req4),
          weather('windrose', String(idSensor), req5),
        ]);

        // Process graph data
        const data4: any = this.processGraphData(data4T);
        this.context?.updateParent({ data1, data4, data5, });
      } catch (error) {
        messageError('Error', getErrorMessage(error));
      }

      this.id = id;

      this.loadingFinish();
      this.hideSidebar();
    }
  };

  public isValidRequest = (data: Record<string, any>): boolean => {
    const { from, to, values } = data;
    if (!from || !to) {
      messageError('Error', 'Please select valid dates for the interval');
      return false;
    }
    if (_.isEmpty(values)) {
      messageError('Error', 'Please select at least a variable to plot');
      return false;
    }

    return true;
  };

  public toTimestamp = (strDate: string): number => {
    const datum: number = Date.parse(strDate);
    return datum / 1000;
  };

  public processGraphData = (dataSrc: any[]): any[] => {
    const { typeVariableArray } = this.state;
    const regex: RegExp = /(\d+)/g;

    return dataSrc.map((e: any, i: number) => {
      let { variableName, variableUnit } = typeVariableArray[parseInt(e.name.match(regex), 10)];
      let data: any[] = [];

      // Pressure conversion from [bars] to [hPa] unit
      if (e.name === 'D05') {
        variableName = 'Pressure';
        variableUnit = 'hPa';
        data = e.data.map((point: any) => { return [this.toTimestamp(point[0]) * 1000, point[1] * 1000]; });
      } else {
        data = e.data.map((point: any) => { return [this.toTimestamp(point[0]) * 1000, point[1]]; });
      }

      return {
        name: `${variableName} [${variableUnit}]`,
        type: 'line',
        data,
        yAxis: (i % 2 === 0) ? 0 : 1,
        zIndex: 2,
      };
    });
  };

  public loadingStart = (): void => {
    this.context?.updateParent({ loading: true, });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.context?.updateParent({ loading: false, });
    window.store.dispatch(finishProcess(this.pid));
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    const { name, value, checked, type, dataset } = (e as React.ChangeEvent<HTMLInputElement>).target;
    const { ctype } = dataset || {};
    // Process new value from ctype
    let newValue: any = (type === 'checkbox') ? !!checked : value;
    if (ctype === 'number') newValue = (/^\d+(\.\d+){0,1}$/.test(value)) ? _.toNumber(value) : null;
    // Create new data state structure
    const levels: string[] = name.split('.');
    const nextState: Record<string, any> = { [levels[0]]: { ...this.state[levels[0]] } };
    _.set(nextState, levels, newValue);
    this.setLocalState(nextState);
  };

  public handleChangeDevice = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    this.setLocalState({ [name]: value });
  };

  public handleChangeDatePicker = (config: DateSelectorConfig): void => {
    const { flatpickrMode } = this.state;

    const hours: string[] = config.getHours() as string[];
    const dates: Date[] = config.getDates() as Date[];
    const newDataIni: moment.Moment = moment(dates[0]);
    const newDataEnd: moment.Moment = (flatpickrMode === 'range') ? moment(dates[1]) : moment(newDataIni).add(1, 'days');

    this.setLocalState({
      dates,
      timeIni: hours[0],
      timeEnd: hours[1],
      dateIni: newDataIni.format('YYYY-MM-DD'),
      dateEnd: newDataEnd.format('YYYY-MM-DD'),
      flatpickrMode: config.getMode(),
    });
  };

  public handleChangeMultiple = (e: EventSelect2): void => {
    const { id } = e.params.data;
    const { selected } = e.params.data;
    if (selected) {
      this.addValue(id);
    } else {
      this.remValue(id);
    }
  };

  /* Management values of multiple select */
  public isValueSelected = (id: string): boolean => {
    const { variables } = this.state || {};
    if (!variables) return false;
    return variables.includes(id);
  };

  public addValue = (id: string): void => {
    if (this.isValueSelected(id)) return;
    const { variables } = this.state || {};
    const selection: any[] = !variables ? [] : variables.slice();
    selection.push(id);
    this.setLocalState({
      variables: selection.sort((a: any, b: any) => {
        return a - b;
      }),
    });
  };

  public remValue = (id: string): void => {
    if (!this.isValueSelected(id)) return;
    const { variables } = this.state || {};
    if (!variables) return;
    const selection: any[] = variables.filter((itemId: any) => itemId !== id);
    this.setLocalState({
      variables: selection,
    });
  };

  public handleSubmit = (): void => {
    this.submit().catch(() => { /* pass */ });
  };

  public onModalOpen = (): void => {
    this.setLocalState({ modalShow: true });
  };

  public onModalClose = (): void => {
    this.setLocalState({ modalShow: false });
  };

  public refresh(create: boolean): void {
    if (create) {
      this.timer = setInterval(() => { this.handleSubmit(); }, 120000);
    } else {
      clearInterval(this.timer);
    }
  }

  public handleAutoRefresh = (autoRefresh: boolean): void => {
    this.setLocalState({
      autoRefresh,
    }, () => { this.refresh(autoRefresh); });
  };

  public render(): JSX.Element {
    const { dates, flatpickrMode, groupDateTime } = this.state;
    const { id, modalShow, autoRefresh, sensorArray, typeVariableArray, variables, groupTime } = this.state;

    const showMainFilter: boolean = (this.props.rowId == null);

    return (
      <div className="main-filters">
        {showMainFilter && (
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <Select2
              name="id"
              value={id}
              onChange={this.handleChange}
              data-ctype="number"
              data={sensorArray.map((item: SensorDO): DataSelect2 => {
                return {
                  id: item.id,
                  text: `${item.description}`,
                  title: `${item.code} - ${item.name}`,
                };
              })}
              options={{ placeholder: 'Select Sensor' }}
              style={{ width: '100%' }}
            />
          </div>
        )}
        <UiValidate>
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <Select2
              multiple
              name="variables"
              value={variables}
              onSelect={this.handleChangeMultiple}
              onUnselect={this.handleChangeMultiple}
              data={typeVariableArray.map((item: TypeSensorVariableDO, idx: number): DataSelect2 => {
                return {
                  id: idx,
                  text: `${item.variableName}`,
                  title: `${item.variableName} [D${idx}] [${item.variableUnit}]`,
                };
              })}
              options={{ placeholder: 'Select variables' }}
              style={{ width: '99%' }}
              required
            />
          </div>
          <DatePicker
            icon
            hours={[]}
            dates={dates}
            showChangeModes
            mode={flatpickrMode}
            granularity={groupDateTime}
            iconClass="wdna-ico-icon-calendar"
            handleChanges={this.handleChangeDatePicker}
          />
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <div>
              <Select2
                name="groupTime"
                value={groupTime}
                onChange={this.handleChange}
                data-ctype="number"
                data={[
                  { id: 0, value: 10, text: '10 Minutes' },
                  { id: 1, value: 30, text: '30 Minutes' },
                  { id: 2, value: 60, text: '1 Hour' },
                  { id: 3, value: 1440, text: 'Day' },
                  { id: 4, value: 0, text: 'Raw Data' },
                ]}
                options={{ placeholder: 'Select Time Interval' }}
                style={{ width: '100%' }}
                required
              />
            </div>
          </div>
          <div className="auto-refresh-button">
            <br />
            <label htmlFor="normal-switch" style={{ marginRight: '20px' }}>
              <Switch
                id="auto-refresh-button"
                className="react-switch"
                aria-labelledby="neat-label"
                height={30}
                onColor="#78a329"
                offColor="#5c6875"
                offHandleColor="#444"
                onHandleColor="#b7f93d"
                checked={autoRefresh}
                onChange={this.handleAutoRefresh}
              />
              <span style={{ marginLeft: '10px' }}>Auto Refresh</span>
            </label>
          </div>
          <div style={{ marginTop: '1px' }}>
            <button type="button" className="filter-btn btn btn-default" onClick={this.onModalOpen}>
              <i className="wdna-ico-icon-playsearch" />
              <span>History Report</span>
            </button>
          </div>
          <Report id={id} show={modalShow} onClose={this.onModalClose} />
          <div style={{ marginTop: '1px' }}>
            <button type="button" className="filter-btn btn btn-default" onClick={this.handleSubmit}>
              <i className="wdna-ico-icon-playsearch" />
              <span>GO</span>
            </button>
          </div>
        </UiValidate>
      </div>
    );
  }
}
